import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiHeart, FiAward, FiUsers, FiClock, FiStar, FiMapPin} = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Made with Love',
      description: 'Every item is crafted with passion and attention to detail by our skilled bakers.'
    },
    {
      icon: FiAward,
      title: 'Quality First',
      description: 'We use only the finest ingredients and traditional recipes to ensure exceptional quality.'
    },
    {
      icon: FiUsers,
      title: 'Community Focused',
      description: 'We are proud to serve the Toronto community with authentic and delicious baked goods.'
    },
    {
      icon: FiStar,
      title: 'Authentic Flavors',
      description: 'Traditional Chinese bakery techniques combined with modern quality standards.'
    }
  ];

  const timeline = [
    {
      year: 'Established',
      title: 'Our Beginning',
      description: 'Tung Hing Bakery opened its doors to serve the Toronto community with authentic Chinese baked goods.'
    },
    {
      year: 'Growth',
      title: 'Building Trust',
      description: 'We built a loyal customer base through consistent quality and authentic flavors.'
    },
    {
      year: 'Today',
      title: 'Serving Excellence',
      description: 'We continue to serve fresh, quality baked goods daily while maintaining our traditional values.'
    }
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen">
      {/* Hero Section - Light yellow header with red circles */}
      <section className="bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black py-20 relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 100, opacity: 0.3 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-16 h-16 bg-[#ff8080] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-20 w-20 h-20 bg-[#ffcc80] rounded-full blur-xl"
          />
          <motion.div
            initial={{ rotate: 0, scale: 0.8 }}
            animate={{ rotate: 360, scale: 1 }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/2 left-1/4 w-24 h-24 bg-[#ff9999] rounded-full opacity-20 blur-2xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.2 }} 
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            About Tung Hing Bakery
          </motion.h1>
          <motion.p 
            initial={{ y: 30, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.4 }} 
            className="text-xl max-w-3xl mx-auto"
          >
            Serving Toronto with authentic Chinese baked goods and traditional flavors
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Tung Hing Bakery has been a cornerstone of the Toronto community, bringing authentic Chinese bakery traditions to Broadview Avenue. Our commitment to quality and authenticity has made us a beloved destination for fresh baked goods and traditional pastries.
              </p>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Every morning, our skilled bakers begin early to prepare fresh items using time-honored recipes and the finest ingredients. From fluffy steamed buns to crispy egg tarts, we maintain the authentic flavors that our customers have come to love and expect.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We take pride in being more than just a bakery – we're a gathering place where families and friends come together to enjoy delicious food and create lasting memories.
              </p>
            </motion.div>
            <motion.div initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }} className="grid grid-cols-2 gap-4">
              <img src="https://i.ibb.co/cS2SVsW3/images-1.jpg" alt="Fresh bakery items" className="rounded-lg shadow-lg" />
              <img src="https://i.ibb.co/xt4fzht6/images-2.jpg" alt="Traditional pastries" className="rounded-lg shadow-lg mt-8" />
              <img src="https://i.ibb.co/zhg375yy/images-3.jpg" alt="Bakery display" className="rounded-lg shadow-lg -mt-8" />
              <img src="https://i.ibb.co/N61r4P60/images-4.jpg" alt="Fresh baked goods" className="rounded-lg shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50 relative overflow-hidden">
        {/* Moving elements - 30% lighter */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: -100, opacity: 0.3 }}
            transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-1/4 right-10 w-14 h-14 bg-[#ff9999] rounded-full blur-xl"
          />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: -80, opacity: 0.3 }}
            transition={{ duration: 12, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-10 left-1/3 w-18 h-18 bg-[#ff8080] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">The principles that guide everything we do at Tung Hing Bakery</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 30, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: index * 0.1 }} 
                className="text-center bg-white p-8 rounded-lg shadow-lg"
              >
                <SafeIcon icon={value.icon} className="w-12 h-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From humble beginnings to becoming a trusted name in Toronto's bakery scene</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {timeline.map((item, index) => (
              <motion.div 
                key={index} 
                initial={{ y: 30, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                transition={{ delay: index * 0.2 }} 
                className="text-center relative"
              >
                <div className="bg-red-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-lg font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600 font-medium mb-3">{item.year}</p>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Hours - Light yellow header with red circles */}
      <section className="py-16 bg-gradient-to-br from-[#ffeb3b] via-[#fff59d] to-[#ffecb3] text-black relative overflow-hidden">
        {/* Red circle templates */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ scale: 0.5, rotate: 0 }}
            animate={{ scale: 1, rotate: 180 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
            className="absolute top-10 left-10 w-32 h-32 bg-[#ff6666] rounded-full opacity-20 blur-3xl"
          />
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: -50, opacity: 0.3 }}
            transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
            className="absolute bottom-20 right-1/4 w-16 h-16 bg-[#ffcc80] rounded-full blur-xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Visit Our Bakery</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6 mr-4 mt-1 flex-shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold">Address</p>
                    <p className="text-black">353 Broadview Ave, Toronto, ON M4M 2H1, Canada</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <SafeIcon icon={FiClock} className="w-6 h-6 mr-4 mt-1 flex-shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold">Hours</p>
                    <p className="text-black">Daily: 7:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Why Choose Us?</h2>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Fresh baked daily</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Authentic Chinese recipes</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Premium quality ingredients</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Friendly customer service</span>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon={FiStar} className="w-5 h-5 mr-3 text-red-600" />
                  <span>Competitive prices</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;